import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark' | 'mixed';

interface ThemeContextType {
  theme: ThemeMode;
  toggleTheme: () => void;
  isDark: boolean;
  isLight: boolean;
  isMixed: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeMode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'light',
}) => {
  const [theme, setTheme] = useState<ThemeMode>(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('studybel-theme') as ThemeMode;
    if (savedTheme && ['light', 'dark', 'mixed'].includes(savedTheme)) {
      return savedTheme;
    }
    
    return defaultTheme;
  });

  // Apply theme changes to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove previous theme classes
    root.classList.remove('light', 'dark', 'mixed');
    
    // Add current theme class
    root.classList.add(theme);
    
    // Update CSS custom properties for space theme
    if (theme === 'dark') {
      root.style.setProperty('--space-bg', '#1A1625');  // Lighter dark purple
      root.style.setProperty('--space-surface', 'rgba(42, 32, 60, 0.85)');  // Much lighter surface
      root.style.setProperty('--space-text-primary', '#F8F6F1');  // Brighter white
      root.style.setProperty('--space-text-secondary', '#D1CCC7');  // Lighter secondary text
      root.style.setProperty('--space-text-muted', '#A8A3A0');  // More visible muted text
      root.style.setProperty('--space-text-bg-primary', '#F8F6F1');  // Same as surface text
      root.style.setProperty('--space-text-bg-secondary', '#D1CCC7');  // Same as surface text
      root.style.setProperty('--space-border', 'rgba(199, 178, 153, 0.25)');  // More visible borders
      root.style.setProperty('--space-shadow', 'rgba(0, 0, 0, 0.4)');  // Softer shadows
      // Navbar and footer same as surfaces in dark theme
      root.style.setProperty('--space-nav-bg', 'rgba(42, 32, 60, 0.85)');
      root.style.setProperty('--space-nav-border', 'rgba(199, 178, 153, 0.25)');
      root.style.setProperty('--space-nav-shadow', 'rgba(0, 0, 0, 0.4)');
      root.style.setProperty('--space-nav-text-primary', '#F8F6F1');
      root.style.setProperty('--space-nav-text-secondary', '#D1CCC7');
    } else if (theme === 'mixed') {
      // Mixed theme: light UI elements with dark space background, but dark navbar/footer
      root.style.setProperty('--space-bg', '#1A1625');  // Dark purple background like dark theme
      root.style.setProperty('--space-surface', 'rgba(255, 255, 255, 0.95)');  // Light surfaces like light theme
      root.style.setProperty('--space-text-primary', '#1A0F2B');  // Dark text for light surfaces
      root.style.setProperty('--space-text-secondary', '#4B5563');  // Light theme secondary text for surfaces
      root.style.setProperty('--space-text-muted', '#6B7280');  // Light theme muted text for surfaces
      root.style.setProperty('--space-text-bg-primary', '#F8F6F1');  // Light text for dark background
      root.style.setProperty('--space-text-bg-secondary', '#D1CCC7');  // Light secondary text for dark background
      root.style.setProperty('--space-border', 'rgba(123, 93, 153, 0.3)');  // Slightly stronger borders for contrast
      root.style.setProperty('--space-shadow', 'rgba(26, 15, 43, 0.15)');  // Light theme shadows
      // Dark navbar and footer in mixed theme
      root.style.setProperty('--space-nav-bg', 'rgba(42, 32, 60, 0.85)');
      root.style.setProperty('--space-nav-border', 'rgba(199, 178, 153, 0.25)');
      root.style.setProperty('--space-nav-shadow', 'rgba(0, 0, 0, 0.4)');
      root.style.setProperty('--space-nav-text-primary', '#F8F6F1');
      root.style.setProperty('--space-nav-text-secondary', '#D1CCC7');
    } else {
      // Light theme
      root.style.setProperty('--space-bg', '#FAFAFA');
      root.style.setProperty('--space-surface', 'rgba(255, 255, 255, 0.9)');
      root.style.setProperty('--space-text-primary', '#1A0F2B');
      root.style.setProperty('--space-text-secondary', '#4B5563');
      root.style.setProperty('--space-text-muted', '#6B7280');
      root.style.setProperty('--space-text-bg-primary', '#1A0F2B');  // Same as surface text
      root.style.setProperty('--space-text-bg-secondary', '#4B5563');  // Same as surface text
      root.style.setProperty('--space-border', 'rgba(123, 93, 153, 0.2)');
      root.style.setProperty('--space-shadow', 'rgba(26, 15, 43, 0.1)');
      // Light navbar and footer same as surfaces in light theme
      root.style.setProperty('--space-nav-bg', 'rgba(255, 255, 255, 0.9)');
      root.style.setProperty('--space-nav-border', 'rgba(123, 93, 153, 0.2)');
      root.style.setProperty('--space-nav-shadow', 'rgba(26, 15, 43, 0.1)');
      root.style.setProperty('--space-nav-text-primary', '#1A0F2B');
      root.style.setProperty('--space-nav-text-secondary', '#4B5563');
    }
    
    // Save to localStorage
    localStorage.setItem('studybel-theme', theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if no manual preference is saved
      const savedTheme = localStorage.getItem('studybel-theme');
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => {
      if (prev === 'light') return 'mixed';
      if (prev === 'mixed') return 'light';
      // If somehow we're in dark mode, switch to light
      return 'light';
    });
  };

  const contextValue: ThemeContextType = {
    theme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    isMixed: theme === 'mixed',
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Space-themed toggle button component
interface ThemeToggleProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = '',
  size = 'md',
}) => {
  const { theme, toggleTheme } = useTheme();
  
  const sizeClasses = {
    sm: 'w-16 h-7',
    md: 'w-20 h-8', 
    lg: 'w-24 h-9',
  };
  
  const circleSizes = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-7 h-7',
  };

  const getThemeConfig = () => {
    switch (theme) {
      case 'light':
        return {
          position: 'translate-x-1',
          icon: '☀️',
          nextTheme: 'mixed'
        };
      case 'mixed':
        return {
          position: size === 'sm' ? 'translate-x-8' : size === 'md' ? 'translate-x-11' : 'translate-x-14',
          icon: '🌌',
          nextTheme: 'light'
        };
      case 'dark':
        // If somehow we're in dark mode, treat as light
        return {
          position: 'translate-x-1',
          icon: '☀️',
          nextTheme: 'mixed'
        };
      default:
        return {
          position: 'translate-x-1',
          icon: '☀️',
          nextTheme: 'mixed'
        };
    }
  };

  const config = getThemeConfig();

  return (
    <button
      onClick={toggleTheme}
      className={`
        relative inline-flex items-center ${sizeClasses[size]} rounded-full p-1
        transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2
        focus:ring-vibrant-orange backdrop-blur-sm ${className}
      `}
      style={{
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 4px 12px rgba(26, 15, 43, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
      }}
      title={`Switch to ${config.nextTheme === 'mixed' ? 'mixed' : 'light'} mode`}
    >
      {/* Active Circle */}
      <div
        className={`
          ${circleSizes[size]} rounded-full transition-all duration-300 ease-in-out
          flex items-center justify-center text-sm transform ${config.position}
          shadow-lg backdrop-blur-sm
        `}
        style={{
          background: theme === 'light' 
            ? 'linear-gradient(135deg, #EFEAE3 0%, #F0F4F8 100%)'
            : theme === 'mixed'
            ? 'linear-gradient(135deg, #7B5D99 0%, #F76700 100%)'
            : 'linear-gradient(135deg, #1A0F2B 0%, #7B5D99 100%)',
          boxShadow: '0 2px 8px rgba(26, 15, 43, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        {config.icon}
      </div>
      
      {/* Background Icons Track */}
      <div className="absolute inset-0 flex items-center justify-between px-3 pointer-events-none">
        <span className={`text-sm transition-all duration-300 ${theme === 'light' ? 'opacity-0' : 'opacity-40'}`}>
          ☀️
        </span>
        <span className={`text-sm transition-all duration-300 ${theme === 'mixed' ? 'opacity-0' : 'opacity-40'}`}>
          🌌
        </span>
      </div>
    </button>
  );
};

export default ThemeProvider; 